import { PromptData } from '@/types/agent';
import { ChatMode } from '@/utils/constants';
import { create } from 'zustand';

export type MessageDeliveryStatus =
  | 'delivering'
  | 'delivered'
  | 'failed'
  | 'interrupted';

export interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: string;
  deliveryStatus?: MessageDeliveryStatus;
  deliveryError?: string;
}

interface ChatState {
  messages: Message[];
  isThinking: boolean;
  isLoading: boolean;
  responseInProgress: boolean;
  guardLocationKey: string | null;
  streamingMessageId: string | null;
  enableTTS: boolean;
  fullAudioMode: boolean;
  showSendButton: boolean;
  playingMessageId: string | null;
  loadingMessageId: string | null;
  chatMode: ChatMode;
  realtimeModeEnabled: boolean;
  selectedPrompt: PromptData | null;
  isInputFocused: boolean; // Track if any input is focused (keyboard open on mobile)
  isTypingOrListening: boolean; // Track if user is typing or listening

  // Actions
  addMessage: (text: string, isUser: boolean, id?: string) => void;
  addMessages: (
    messages: {
      id: string;
      text: string;
      isUser: boolean;
      timestamp: string;
    }[],
    newMessages?: boolean
  ) => void;
  updateMessage: (messageId: string, text: string) => void;
  removeMessage: (messageId: string) => void;
  setMessageDeliveryStatus: (
    messageId: string,
    status: MessageDeliveryStatus,
    error?: string
  ) => void;
  setThinking: (thinking: boolean) => void;
  setResponseInProgress: (inProgress: boolean) => void;
  setStreamingMessageId: (messageId: string | null) => void;
  finishAssistantResponse: () => void;
  setEnableTTS: (enable: boolean) => void;
  setFullAudioMode: (enable: boolean) => void;
  setShowSendButton: (show: boolean) => void;
  setPlayingMessageId: (id: string | null) => void;
  setLoadingMessageId: (id: string | null) => void;
  clearMessages: () => void;
  setChatMode: (mode: ChatMode) => void;
  setRealtimeModeEnabled: (enabled: boolean) => void;
  setLoading: (loading: boolean) => void;
  setSelectedPrompt: (prompt: PromptData | null) => void;
  setInputFocused: (focused: boolean) => void;
  setTypingOrListening: (active: boolean) => void;
}

function getCurrentGuardLocationKey() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function hasDeliveringUserMessage(messages: Message[]) {
  return messages.some(
    (message) => message.isUser && message.deliveryStatus === 'delivering'
  );
}

export const selectIsGenerationGuardBlocked = (state: ChatState) =>
  state.responseInProgress ||
  state.isThinking ||
  state.streamingMessageId !== null ||
  hasDeliveringUserMessage(state.messages);

export const getGenerationGuardBlockedPath = (state: ChatState) => {
  if (state.guardLocationKey) return state.guardLocationKey;
  if (!selectIsGenerationGuardBlocked(state)) return null;
  return getCurrentGuardLocationKey();
};

export const useChatStore = create<ChatState>((set) => ({
  messages: [],
  isThinking: false,
  isLoading: false,
  responseInProgress: false,
  guardLocationKey: null,
  streamingMessageId: null,
  enableTTS: false,
  fullAudioMode: false,
  showSendButton: true,
  playingMessageId: null,
  loadingMessageId: null,
  chatMode: ChatMode.TEXT,
  realtimeModeEnabled: true,
  selectedPrompt: null,
  isInputFocused: false,
  isTypingOrListening: false,

  addMessage: (text: string, isUser: boolean, messageId?: string) =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: messageId || Date.now().toString(),
          text,
          isUser,
          timestamp: new Date().toISOString(),
        },
      ],
    })),
  addMessages: (messages, newMessages = false) =>
    set((state) => ({
      messages: newMessages ? messages : [...messages, ...state.messages],
    })),
  updateMessage: (messageId: string, text: string) =>
    set((state) => ({
      messages: state.messages.map((message) =>
        message.id === messageId ? { ...message, text } : message
      ),
    })),
  removeMessage: (messageId: string) =>
    set((state) => ({
      messages: state.messages.filter((message) => message.id !== messageId),
    })),
  setMessageDeliveryStatus: (messageId, status, error) =>
    set((state) => {
      const messages = state.messages.map((message) =>
        message.id === messageId
          ? {
              ...message,
              deliveryStatus: status,
              deliveryError: error,
            }
          : message
      );

      if (status !== 'delivering') {
        return { messages };
      }

      return {
        messages,
        responseInProgress: true,
        guardLocationKey:
          state.guardLocationKey ?? getCurrentGuardLocationKey(),
      };
    }),
  setThinking: (thinking) => set({ isThinking: thinking }),
  setResponseInProgress: (inProgress) => {
    if (inProgress) {
      set({
        responseInProgress: true,
        guardLocationKey: getCurrentGuardLocationKey(),
      });
      return;
    }

    set({ responseInProgress: false, guardLocationKey: null });
  },
  setStreamingMessageId: (messageId) => set({ streamingMessageId: messageId }),
  finishAssistantResponse: () =>
    set({
      responseInProgress: false,
      guardLocationKey: null,
      isThinking: false,
      streamingMessageId: null,
    }),
  setEnableTTS: (enable) => set({ enableTTS: enable }),
  setFullAudioMode: (enable) => set({ fullAudioMode: enable }),
  setShowSendButton: (show) => set({ showSendButton: show }),
  setPlayingMessageId: (id) => set({ playingMessageId: id }),
  setLoadingMessageId: (id) => set({ loadingMessageId: id }),
  clearMessages: () =>
    set({
      messages: [],
      responseInProgress: false,
      guardLocationKey: null,
      isThinking: false,
      streamingMessageId: null,
    }),
  setChatMode: (mode) => set({ chatMode: mode }),
  setRealtimeModeEnabled: (enabled) => set({ realtimeModeEnabled: enabled }),
  setLoading: (loading) => set({ isLoading: loading }),
  setSelectedPrompt: (prompt) => set({ selectedPrompt: prompt }),
  setInputFocused: (focused) => set({ isInputFocused: focused }),
  setTypingOrListening: (active) => set({ isTypingOrListening: active }),
}));

// Selector to get computed isInputActive (combines focus and typing/listening)
export const selectIsInputActive = (state: ChatState) =>
  state.isInputFocused || state.isTypingOrListening;
